import {
  fetchServiceJson,
  isFeatureServerUrl,
  isMapServerUrl,
  stripLayerId,
} from './arcgisClient';
import type { ServiceType } from './arcgisTypes';

/**
 * Introspect a public ArcGIS REST service URL and classify it.
 *
 * - `'feature'`        → FeatureServer (Tier 1)
 * - `'map-queryable'`  → MapServer whose layer(s) support `/query` (Tier 2)
 * - `'unsupported'`    → non-queryable MapServer, tiled cache, ImageServer,
 *                        or anything else
 *
 * The caller should display a rejection toast on `'unsupported'`.
 */
export const detectService = async (url: string): Promise<ServiceType> => {
  const trimmed = url.trim();

  // -------------------------------------------------------------------------
  // Tier 1 — FeatureServer: confirm the service actually resolves. A URL can
  // look like a FeatureServer yet point at a bad host, org id, or service name;
  // fetching the metadata (throws on network/HTTP failure, `error` block on a
  // bad service) is the only way to reject those.
  // -------------------------------------------------------------------------
  if (isFeatureServerUrl(trimmed)) {
    const serviceJson = await fetchServiceJson(trimmed);

    if (serviceJson.error) {
      return 'unsupported';
    }

    return 'feature';
  }

  // -------------------------------------------------------------------------
  // Tier 2 — MapServer: must verify it's queryable (not a tiled cache)
  // -------------------------------------------------------------------------
  if (isMapServerUrl(trimmed)) {
    const serviceJson = await fetchServiceJson(trimmed);

    if (serviceJson.error) {
      return 'unsupported';
    }

    // Tiled / cached MapServers cannot be queried as vector.
    if (serviceJson.singleFusedMapCache || serviceJson.tileInfo) {
      return 'unsupported';
    }

    // If the URL already points at a specific layer (e.g. /MapServer/0),
    // check `supportsQuery` on that layer's JSON directly.
    if (serviceJson.supportsQuery === true) {
      return 'map-queryable';
    }

    // If the URL points at the service root, check whether any of the listed
    // layers advertises `supportsQuery: true`.
    if (Array.isArray(serviceJson.layers) && serviceJson.layers.length > 0) {
      // The service-root JSON doesn't include per-layer supportsQuery; we
      // fetch layer 0 to check.  Most queryable MapServers support query on
      // every dynamic layer, so checking layer 0 is representative.
      const rootUrl = stripLayerId(trimmed);
      const layer0Json = await fetchServiceJson(`${rootUrl}/0`);
      if (layer0Json.supportsQuery === true) {
        return 'map-queryable';
      }
    }

    return 'unsupported';
  }

  // Anything else (ImageServer, GeocodeServer, etc.) is unsupported.
  return 'unsupported';
};
